import { useMemo, useState } from 'react';
import { useDrivers } from './hooks';
import type { Driver } from '@matix/types';
import { useLang } from '../../lib/language';
import { nameFor } from '../../lib/localizedName';

/**
 * DriverSelect — searchable dropdown sourced from /api/v1/drivers.
 *
 * Behaviour:
 *   - Loads once when the modal mounts; cached for 5 min via react-query
 *     (drivers don't change often during a shift).
 *   - Client-side name filter (lowercased contains).
 *   - Empty list → empty-state message + CREATE stays disabled (handled
 *     by the modal via the deliveryDraft.driverId check).
 */
export function DriverSelect(props: {
  value: number | null;
  onChange: (id: number | null) => void;
  disabled?: boolean;
}) {
  const { t, lang } = useLang();
  const { data, isLoading, isError } = useDrivers();
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState('');

  const drivers: Driver[] = data?.data ?? [];
  const selected = useMemo(
    () => drivers.find((d) => d.id === props.value) ?? null,
    [drivers, props.value],
  );

  const filtered = useMemo(() => {
    const needle = filter.trim().toLowerCase();
    if (!needle) return drivers;
    return drivers.filter((d) =>
      ((d.name ?? '') + ' ' + (d.name_ar ?? '')).toLowerCase().includes(needle),
    );
  }, [drivers, filter]);

  if (isLoading) {
    return <div className="driver-select loading">{t({ en: 'Loading drivers…', ar: 'جارٍ تحميل السائقين…' })}</div>;
  }
  if (isError) {
    return <div className="driver-select error">{t({ en: 'Couldn’t load drivers.', ar: 'تعذّر تحميل السائقين.' })}</div>;
  }
  if (drivers.length === 0) {
    return (
      <div className="driver-select empty">
        {t({ en: 'No drivers configured. Contact admin.', ar: 'لا يوجد سائقون مُعرّفون. تواصل مع المسؤول.' })}
      </div>
    );
  }

  /**
   * The driver's name in the till's language.
   *
   * It used to render both scripts at once — "Ahmed (أحمد)" — which is how a
   * picker looks when nobody has decided which language it is in. Everywhere
   * else a driver appears (the order card, the detail drawer, the dispatch
   * toast) now follows the language, and a picker that disagreed with the board
   * would be the one place a cashier had to translate for themselves.
   *
   * `nameFor` falls back to the other script rather than to nothing: plenty of
   * legacy rows have only one of the two filled in, and an Arabic till must
   * still be able to pick a driver whose Arabic name was never entered.
   */
  function display(d: Driver | null): string {
    if (!d) return t({ en: 'select Driver', ar: 'اختر السائق' });
    return nameFor(lang, { name: d.name, name_ar: d.name_ar }, `#${d.id}`);
  }

  return (
    <div className={`driver-select ${open ? 'open' : ''}`}>
      <button
        type="button"
        className="driver-select-trigger"
        disabled={props.disabled}
        onClick={() => setOpen((o) => !o)}
      >
        {display(selected)}
        <span className="caret" aria-hidden>▾</span>
      </button>
      {open && (
        <div className="driver-select-menu" role="listbox">
          <input
            className="driver-select-search"
            placeholder={t({ en: 'Search…', ar: 'بحث…' })}
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            autoFocus
          />
          <ul className="driver-select-list">
            {filtered.map((d) => (
              <li
                key={d.id}
                role="option"
                aria-selected={d.id === props.value}
                className={`driver-select-option ${d.id === props.value ? 'selected' : ''}`}
                onClick={() => {
                  props.onChange(d.id);
                  setOpen(false);
                  setFilter('');
                }}
              >
                <div className="driver-name">{display(d)}</div>
                {d.phone && <div className="driver-phone">{d.phone}</div>}
              </li>
            ))}
            {filtered.length === 0 && (
              <li className="driver-select-empty">{t({ en: 'No matches', ar: 'لا توجد نتائج' })}</li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
}
